<template>
  <v-menu
    :model-value="openMenu"
    @update:modelValue="onMenuChange"
    max-width="400"
    location="right center"
    :close-on-content-click="false"
    @click:outside="openMenu = false"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <div v-bind="activatorProps" @click="openMenu = !openMenu" style="line-height: 0;">
        <slot></slot>
      </div>
    </template>
    <template v-slot:default>
      <v-card class="ml-2 pa-3" width="300">
        <div class="d-flex pb-4 justify-space-between">
          <v-btn
            :class="{ 'icon-hidden': !openCreateStep }"
            variant="text"
            size="small"
            icon="mdi-arrow-left"
            @click="openCreateStep = false"
          />
          <span class="text-h7 font-weight-bold pt-2">Entities</span>
          <v-btn
            variant="text"
            size="small"
            icon="mdi-close"
            @click="openMenu = false"
          />
        </div>
        <v-text-field
          v-if="!store.state.isWorkflowDisabled"
          :rules="[maxNameLength]"
          class="select-entity"
          autofocus
          density="compact"
          v-model="search"
          :placeholder="openCreateStep ? 'Name the entity' : 'Search for entity'"
          variant="outlined"
          data-test="select-entity-search"
        />
        <v-textarea
          v-if="openCreateStep && !store.state.isWorkflowDisabled"
          auto-grow
          rows="3"
          class="no-border"
          variant="outlined"
          v-model="aiDescription"
          placeholder="Description (optional)"
        />

        <v-list>
          <template v-for="(entity) in filteredEntities" :key="entity.id">
            <v-tooltip
              v-if="!openCreateStep && (allowReselectCurrent || entity.id !== selectedEntity?.id)"
              location="left"
              :disabled="!isEntityDisabled(entity.id) || !disabledTooltip"
            >
              <template v-slot:activator="{ props: tipProps }">
                <v-list-item
                  v-bind="tipProps"
                  density="compact"
                  :disabled="store.state.isWorkflowDisabled || isEntityDisabled(entity.id)"
                  @click="onItemClick(entity)"
                  class="entity-row"
                  :class="{
                    'entity-row-disabled': isEntityDisabled(entity.id),
                    'entity-row-workflow-disabled': store.state.isWorkflowDisabled
                  }"
                >
                  <v-list-item-title class="d-flex align-center justify-space-between">
                    {{ entity.name }}
                    <div
                      v-if="!isEntityDisabled(entity.id) && !store.state.isWorkflowDisabled"
                      class="d-flex align-center ga-1 entity-row-actions"
                    >
                      <edit-entity :entity="entity" />
                      <v-progress-circular
                        v-if="deleteEntityLoading"
                        indeterminate
                        size="16"
                        width="2"
                      />
                      <v-tooltip v-else location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            v-bind="props"
                            size="16"
                            color="grey"
                            class="cursor-pointer"
                            @click.stop
                            @click="() => onDeleteEntity(entity)"
                            loading
                          >
                            mdi-delete
                          </v-icon>
                        </template>
                        Delete
                      </v-tooltip>
                    </div>
                  </v-list-item-title>
                </v-list-item>
              </template>
              <span>{{ disabledTooltip }}</span>
            </v-tooltip>
          </template>
          <v-divider class="my-2"></v-divider>
          <v-list-item
            density="compact"
            @click="onCreateEntity"
            :disabled="store.state.isWorkflowDisabled || hasMatchingEntity || (openCreateStep && !search)"
          >
            <v-list-item-title class="d-flex align-center ga-2">
              {{ openCreateStep ? 'Create' : 'Create a new entity with AI' }}
              <v-icon color="#787878">mdi-creation</v-icon>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </template>
  </v-menu>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import EditEntity from '@/components/EditEntity.vue'
import useEntityActions from '@/composables/useEntityActions'
import useGenerateEntity from '@/composables/useGenerateEntity'
import { SchemaType } from '@/constants/schema'

const { entityId, allowReselectCurrent, disabledEntityIds, disabledTooltip } = defineProps({
  entityId: String,
  allowReselectCurrent: Boolean,
  disabledEntityIds: { type: Array, default: () => [] },
  disabledTooltip: { type: String, default: '' }
})
const emit = defineEmits(['onSelectEntity', 'onClose'])

const openMenu = ref(false)
const openCreateStep = ref(false)
const deleteEntityLoading = ref(false)
const search = ref('')
const aiDescription = ref('')
const { generateEntity } = useGenerateEntity()
const { deleteEntity } = useEntityActions()

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

defineExpose({ openMenu })

const selectedEntity = computed(() => {
  return (workflow.value.schemas ?? []).find((entity) => entity.id === entityId)
})

const filteredEntities = computed(() => {
  return (workflow.value.schemas ?? []).filter((schema) => {
    return schema.type === SchemaType.ENTITY && schema.name.toLowerCase().includes(search.value.toLowerCase())
  })
})
const hasMatchingEntity = computed(() => {
  return (workflow.value.schemas ?? []).some((entity) => {
    return entity.type === SchemaType.ENTITY && entity.name.toLowerCase() === search.value.toLowerCase()
  })
})

const onCreateEntity = async () => {
  if (!openCreateStep.value) {
    openCreateStep.value = true
    return
  }

  const newEntity = await generateEntity({
    name: search.value.trim(),
    description: aiDescription.value.trim()
  })

  if (newEntity?.name) {
    search.value = newEntity.name
    openCreateStep.value = false
  }
}

const maxNameLength = (name) => {
  return name.length <= 30 || 'Name must be less than 30 characters'
}

const isEntityDisabled = (id) => disabledEntityIds.includes(id)

const onItemClick = (entity) => {
  if (store.state.isWorkflowDisabled || isEntityDisabled(entity.id)) {
    return
  }

  onSelectEntity(entity)
}

const onSelectEntity = async (entity) => {
  emit('onSelectEntity', entity)
  openMenu.value = false
}

const onMenuChange = (isOpen) => {
  if (!isOpen) {
    emit('onClose')
    setTimeout(() => {
      // Timeout for the menu to finish close animation before resetting
      // the search value (v-menu does not support natively)
      search.value = ''
      aiDescription.value = ''
      openCreateStep.value = false
    }, 250)
  }
}

const onDeleteEntity = async (entity) => {
  if (deleteEntityLoading.value) {
    return
  }

  deleteEntityLoading.value = true
  await deleteEntity(entity.id)
  deleteEntityLoading.value = false
}
</script>

<style scoped>
::v-deep(.v-chip) {
  .v-chip__content {
    font-size: 12px;
  }

  .remove-entity-icon {
    font-size: 0 !important;
    width: 0 !important;
    transition: all 0.2s;
  }

  &:hover {
    .remove-entity-icon {
      font-size: 16px !important;
      width: 16px !important;
    }
  }
}

.icon-hidden {
  visibility: hidden;
}

.entity-row {
  .entity-row-actions {
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    .entity-row-actions {
      opacity: 1;
    }
  }
}

.entity-row-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.entity-row-workflow-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
